import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { type UseInfiniteQueryResult } from "@tanstack/react-query";

import { RobotoTableCell, RobotoTableRow } from "@/shared/components";
import { FormattedDateTime } from "@/shared/datetime";
import {
  eventLabel,
  type TriggerDispatchRecord,
  type TriggerTargetSpec,
} from "@/shared/domain/triggers";
import { type PaginatedResponse } from "@/shared/http";

import { TriggerLink } from "../links/TriggerLink";

import { DispatchResultLink } from "./DispatchResultLink";
import { DispatchStatusChip } from "./DispatchStatusChip";
import { DispatchTargetCell } from "./DispatchTargetCell";

type DispatchesQuery = UseInfiniteQueryResult<
  { pages: PaginatedResponse<TriggerDispatchRecord>[] },
  Error
>;

interface TriggerDispatchesTableProps {
  query: DispatchesQuery;
  /** Shown on the dataset page, where rows span many triggers. */
  showTriggerColumn?: boolean;
  /**
   * The trigger's current targets, when every row belongs to one trigger. Lets
   * the Target column name what ran instead of printing its opaque id.
   */
  targets?: readonly TriggerTargetSpec[];
  emptyMessage?: string;
}

/**
 * Keyset-paged table of trigger dispatches: when a trigger fired, for which
 * event, which target ran, how it went, and what it produced.
 */
export function TriggerDispatchesTable({
  query,
  showTriggerColumn = false,
  targets,
  emptyMessage = "No dispatches yet. When this trigger fires, each dispatched target appears here.",
}: TriggerDispatchesTableProps) {
  const theme = useTheme();

  if (query.isLoading) {
    return <CircularProgress size="1.5rem" />;
  }

  if (query.isError) {
    return <Alert severity="error">{query.error.message}</Alert>;
  }

  const dispatches = query.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <RobotoTableCell>Time</RobotoTableCell>
              {showTriggerColumn && <RobotoTableCell>Trigger</RobotoTableCell>}
              <RobotoTableCell>Platform event</RobotoTableCell>
              <RobotoTableCell>Target</RobotoTableCell>
              <RobotoTableCell>Status</RobotoTableCell>
              <RobotoTableCell>Result</RobotoTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dispatches.length === 0 ? (
              <RobotoTableRow>
                <RobotoTableCell colSpan={showTriggerColumn ? 6 : 5}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </RobotoTableCell>
              </RobotoTableRow>
            ) : (
              dispatches.map((dispatch) => (
                <RobotoTableRow
                  key={`${dispatch.trigger_id}:${dispatch.idempotency_token}:${dispatch.target_id}`}
                >
                  <RobotoTableCell>
                    <span title={dispatch.idempotency_token}>
                      <FormattedDateTime
                        value={dispatch.claimed_at}
                        showTooltip={false}
                      />
                    </span>
                  </RobotoTableCell>
                  {showTriggerColumn && (
                    <RobotoTableCell>
                      <TriggerLink triggerId={dispatch.trigger_id} />
                    </RobotoTableCell>
                  )}
                  <RobotoTableCell>
                    {eventLabel(dispatch.event_type)}
                  </RobotoTableCell>
                  <RobotoTableCell>
                    <DispatchTargetCell dispatch={dispatch} targets={targets} />
                  </RobotoTableCell>
                  <RobotoTableCell>
                    <DispatchStatusChip
                      status={dispatch.status}
                      detail={dispatch.status_detail}
                    />
                  </RobotoTableCell>
                  <RobotoTableCell>
                    <DispatchResultLink dispatch={dispatch} />
                  </RobotoTableCell>
                </RobotoTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {query.hasNextPage && (
        <Box sx={{ mt: theme.spacing(1) }}>
          <Button
            size="small"
            variant="outlined"
            disabled={query.isFetchingNextPage}
            onClick={() => void query.fetchNextPage()}
            data-cy="dispatches-load-more"
          >
            {query.isFetchingNextPage ? "Loading…" : "Load more"}
          </Button>
        </Box>
      )}
    </>
  );
}
